import Interncv from "./interncv";
import "./CardList.css";
import { useState, useEffect, useCallback } from "react";

// Local JSON data imports
import { internCVList } from "./data_intern";
import { placementCVList } from "./data_placement";

// Firebase imports (kept for toggle fallback)
import { db } from "./useFirebase";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs,
  getCountFromServer,
} from "firebase/firestore";

// ─────────────────────────────────────────────────
// DATA SOURCE TOGGLE
// Set USE_JSON = true  → use local data files (data_intern.js / data_placement.js)
// Set USE_JSON = false → use Firebase Firestore (original behaviour)
// ─────────────────────────────────────────────────
const USE_JSON = true;

const ITEMS_PER_PAGE = 24;
const SEARCH_LIMIT = ITEMS_PER_PAGE;

const CardList = () => {
  const [toggleState, setToggleState] = useState(1);
  const [state, setState] = useState({
    products: [],
    filters: "all",
    lastVisibleArr: [],
    totalCount: 0,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounce Search Input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500); // 500ms delay

    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);

  // Effect to trigger fetch when Debounced Search or Toggle State Changes
  useEffect(() => {
    // Reset page to 1 on new search or tab switch
    setCurrentPage(1);
    setState((prev) => ({ ...prev, lastVisibleArr: [] }));
    fetchCVs(toggleState, 1, state.filters, debouncedSearch);
    // eslint-disable-next-line
  }, [debouncedSearch, toggleState, state.filters]);

  // ─── JSON / LOCAL MODE ────────────────────────────────────────────────────
  const fetchCVsFromJSON = useCallback(
    (type, page = 1, filter = "all", searchTerm = "") => {
      setLoading(true);
      try {
        const rawList = type === 1 ? internCVList : placementCVList;

        // 1. Apply category filter
        let filtered =
          filter === "all"
            ? rawList
            : rawList.filter(
                (cv) => cv.category && cv.category.toLowerCase() === filter.toLowerCase(),
              );

        // 2. Apply search (case-insensitive substring on name_ and company)
        if (searchTerm.trim() !== "") {
          const q = searchTerm.toLowerCase();
          filtered = filtered.filter(
            (cv) =>
              (cv.name_ && cv.name_.toLowerCase().includes(q)) ||
              (cv.company && cv.company.toLowerCase().includes(q)),
          );
        }

        const totalCount = filtered.length;

        // 3. Paginate
        const start = (page - 1) * ITEMS_PER_PAGE;
        const products = filtered.slice(start, start + ITEMS_PER_PAGE);

        setState((prev) => ({
          filters: filter,
          products,
          lastVisibleArr: prev.lastVisibleArr,
          totalCount,
        }));
      } catch (error) {
        console.error("Error loading local CVs:", error);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  // ─── FIREBASE MODE ────────────────────────────────────────────────────────
  const fetchCVsFromFirebase = useCallback(
    async (type, page = 1, filter = "all", searchTerm = "") => {
      setLoading(true);
      try {
        let typeStr = type === 1 ? "internship" : "placement";
        let products = [];
        let totalCount = 0;
        let lastVisible = null;

        if (searchTerm.trim() !== "") {
          // --- SEARCH MODE (Server-Side) ---
          const formattedSearch =
            searchTerm.charAt(0).toUpperCase() + searchTerm.slice(1).toLowerCase();
          const endSearch = formattedSearch + "\uf8ff";

          const baseConstraints = [where("type", "==", typeStr)];
          if (filter !== "all") {
            baseConstraints.push(where("category", "==", filter));
          }

          const companyQuery = query(
            collection(db, "cvs_new"),
            ...baseConstraints,
            where("company", ">=", formattedSearch),
            where("company", "<=", endSearch),
            limit(SEARCH_LIMIT),
          );

          const nameQuery = query(
            collection(db, "cvs_new"),
            ...baseConstraints,
            where("name_", ">=", formattedSearch),
            where("name_", "<=", endSearch),
            limit(SEARCH_LIMIT),
          );

          const [companySnap, nameSnap] = await Promise.all([
            getDocs(companyQuery),
            getDocs(nameQuery),
          ]);

          const mergedMap = new Map();
          const processDoc = (doc) => {
            mergedMap.set(doc.id, { id: doc.id, ...doc.data() });
          };

          companySnap.forEach(processDoc);
          nameSnap.forEach(processDoc);

          products = Array.from(mergedMap.values());

          products.sort((a, b) => {
            const getTime = (date) => {
              if (!date) return 0;
              if (date.seconds) return date.seconds;
              if (date instanceof Date) return Math.floor(date.getTime() / 1000);
              return 0;
            };
            return getTime(b.createdAt) - getTime(a.createdAt);
          });

          products = products.slice(0, ITEMS_PER_PAGE);
          totalCount = products.length;
        } else {
          // --- BROWSE MODE (Standard Pagination) ---
          let queryConstraints = [
            where("type", "==", typeStr),
            orderBy("createdAt", "desc"),
            limit(ITEMS_PER_PAGE),
          ];

          if (filter !== "all") {
            queryConstraints.unshift(where("category", "==", filter));
          }

          if (page > 1 && state.lastVisibleArr[page - 2]) {
            queryConstraints.push(startAfter(state.lastVisibleArr[page - 2]));
          }

          let cvsQuery = query(collection(db, "cvs_new"), ...queryConstraints);
          const querySnapshot = await getDocs(cvsQuery);

          querySnapshot.forEach((doc) => {
            products.push({ id: doc.id, ...doc.data() });
          });

          lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1];

          if (page === 1) {
            let countQueryConstraints = [where("type", "==", typeStr)];
            if (filter !== "all") {
              countQueryConstraints.push(where("category", "==", filter));
            }
            let countQuery = query(collection(db, "cvs_new"), ...countQueryConstraints);
            const countSnapshot = await getCountFromServer(countQuery);
            totalCount = countSnapshot.data().count;
          } else {
            totalCount = state.totalCount;
          }
        }

        setState((prev) => {
          let lastVisibleArr = [...prev.lastVisibleArr];
          if (lastVisible) lastVisibleArr[page - 1] = lastVisible;

          return {
            filters: filter,
            products,
            lastVisibleArr,
            totalCount,
          };
        });
      } catch (error) {
        console.error("Error fetching CVs:", error);
      } finally {
        setLoading(false);
      }
    },
    [state.lastVisibleArr, state.totalCount],
  );

  // ─── UNIFIED FETCH (routes to correct implementation) ────────────────────
  const fetchCVs = useCallback(
    (type, page = 1, filter = "all", searchTerm = "") => {
      if (USE_JSON) {
        fetchCVsFromJSON(type, page, filter, searchTerm);
      } else {
        fetchCVsFromFirebase(type, page, filter, searchTerm);
      }
    },
    [fetchCVsFromJSON, fetchCVsFromFirebase],
  );

  const toggleTab = useCallback((index) => {
    setToggleState(index);
    setSearchQuery(""); // Clear search
  }, []);

  const CATEGORIES = ["all", "software", "consultancy", "data", "product", "core", "quant", "finance"];

  const handleFilterChange = (category) => {
    const newFilter = category === state.filters ? "all" : category;
    setState((prev) => ({ ...prev, filters: newFilter, lastVisibleArr: [] }));
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    const totalPages = Math.ceil(state.totalCount / ITEMS_PER_PAGE);
    if (debouncedSearch === "" && page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      fetchCVs(toggleState, page, state.filters, debouncedSearch);
    }
  };

  const totalItems = state.totalCount;
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
  const isSearching = debouncedSearch !== "";

  return (
    <div className='cv-repo-container'>
      {/* Tabs */}
      <div className='tabs-container'>
        <button className={`tab-btn ${toggleState === 1 ? "active" : ""}`} onClick={() => toggleTab(1)}>
          Internships
        </button>
        <button className={`tab-btn ${toggleState === 2 ? "active" : ""}`} onClick={() => toggleTab(2)}>
          Placement
        </button>
      </div>

      {/* Controls */}
      <div className='controls-section'>
        <div className='search-box-wrapper'>
          <svg
            className='search-icon-svg'
            width='20'
            height='20'
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2'>
            <circle cx='11' cy='11' r='8'></circle>
            <path d='m21 21-4.35-4.35'></path>
          </svg>
          <input
            type='text'
            className='search-input'
            placeholder='Search by name or company...'
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className='filter-chips-wrapper'>
          {CATEGORIES.map((cat) => (
            <div
              key={cat}
              className={`filter-chip ${state.filters === cat ? "active" : ""}`}
              onClick={() => handleFilterChange(cat)}>
              {cat}
            </div>
          ))}
        </div>
      </div>

      {/* Grid Content */}
      <div className='cv-grid-wrapper'>
        {loading ?
          <div className='loading-container'>
            <img
              src={process.env.PUBLIC_URL + "/assets/images/13525-empty.gif"}
              alt='Loading...'
              style={{ width: "64px", height: "64px" }}
            />
          </div>
        : <>
            {state.products.length === 0 ?
              <div className='empty-state'>
                <h3>No CVs found</h3>
                <p>Try adjusting your search or filters</p>
              </div>
            : <Interncv blogs={state.products} />}
          </>
        }
      </div>

      {/* Pagination */}
      {!loading && !isSearching && totalPages > 1 && (
        <div className='pagination-wrapper'>
          <button className='page-btn' onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
            &laquo; Prev
          </button>
          {(() => {
            const generatePages = () => {
              if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);

              const pages = new Set([1, totalPages]);
              if (currentPage > 1) pages.add(currentPage - 1);
              pages.add(currentPage);
              if (currentPage < totalPages) pages.add(currentPage + 1);

              const sorted = Array.from(pages).sort((a, b) => a - b);
              const withDots = [];
              let prev = null;

              sorted.forEach((p) => {
                if (prev && p - prev > 1) {
                  withDots.push("...");
                }
                withDots.push(p);
                prev = p;
              });

              return withDots;
            };

            return generatePages().map((p, i) =>
              p === "..." ?
                <span key={`dots-${i}`} style={{ padding: "0 5px", color: "#6c757d" }}>
                  ...
                </span>
              : <button
                  key={p}
                  onClick={() => handlePageChange(p)}
                  className={`page-btn ${currentPage === p ? "active" : ""}`}>
                  {p}
                </button>,
            );
          })()}

          <button
            className='page-btn'
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}>
            Next &raquo;
          </button>
        </div>
      )}

      {/* Search Result Count Indicator */}
      {!loading && isSearching && (
        <div style={{ textAlign: "center", marginTop: "1rem", color: "#6c757d" }}>
          Showing top {state.products.length} results
        </div>
      )}
    </div>
  );
};

export default CardList;
